import React, { createContext, FC, ReactNode, useContext, useMemo, useState } from 'react';
import { useConfig } from '../utils';
import { markdown } from '../utils/markdown';
// eslint-disable-next-line import/extensions
import en_gb from './en-GB.json';
// eslint-disable-next-line import/extensions
import en_us from './en-US.json';
// eslint-disable-next-line import/extensions
import pt_br from './pt-BR.json';

type DictKey = keyof typeof en_us;

/**
 * Allows prototyping with a `__` prefixed key.
 */
export type TranslationKey = DictKey | `__${string}`;

type PartialDictionary = Partial<Record<TranslationKey, string>>;

const full_dictionaries = {
  'en-US': en_us,
  'pt-BR': pt_br,
  'en-GB': en_gb,
} as const satisfies Readonly<Record<string, PartialDictionary>>;

const short_dictionaries: Readonly<Record<string, PartialDictionary>> = {
  en: en_us,
  pt: pt_br,
};

const dictionaries: Readonly<Record<string, PartialDictionary>> = {
  ...full_dictionaries,
  ...short_dictionaries,
};

const availableLanguages = Object.entries(full_dictionaries).map(([k, v]) => ({
  key: k,
  label: v.language_name,
}));

const lookupKey = (
  key: string,
  dictionary: PartialDictionary,
  i18nOverrides?: Record<string, string>,
): string => {
  return (
    i18nOverrides?.[key] ??
    dictionary[key as DictKey] ??
    (key in en_us ? en_us[key as DictKey] : key.replace(/^__/, ''))
  );
};

function translatorGenerator(
  dictionary: PartialDictionary,
  i18nOverrides?: Record<string, string>,
): (key: TranslationKey, ...params: (string | number)[]) => string {
  return (key: TranslationKey, ...params: (string | number)[]) => {
    let translated: string = lookupKey(key, dictionary, i18nOverrides);

    for (const [placeholder, value] of params.entries()) {
      translated = translated.replaceAll(`%%${placeholder}`, String(value));
    }

    return translated;
  };
}

const escapeMarkdown = (text: string): string => {
  return text.replaceAll(/([!#()*+.>[\\\]_`{|}~-])/g, String.raw`\$1`);
};

function translatorGeneratorMardown(
  dictionary: PartialDictionary,
  i18nOverrides?: Record<string, string>,
): (key: TranslationKey, ...params: (string | number)[]) => React.JSX.Element {
  // Basic cache, we only cache static strings without interpolation for simplicity for now.
  const cache = new Map<string, string>();
  // eslint-disable-next-line react/display-name
  return (key: TranslationKey, ...params: (string | number)[]): React.JSX.Element => {
    let translated: string;

    if (cache.has(key)) {
      // eslint-disable-next-line react/no-danger
      return <span dangerouslySetInnerHTML={{ __html: cache.get(key)! }} />;
    }

    translated = lookupKey(key, dictionary, i18nOverrides);
    for (const [placeholder, value] of params.entries()) {
      translated = translated.replaceAll(`%%${placeholder}`, escapeMarkdown(String(value)));
    }

    const rendered = markdown(translated, true);
    if (params.length > 0) {
      cache.set(key, rendered);
    }

    // eslint-disable-next-line react/no-danger
    return <span dangerouslySetInnerHTML={{ __html: rendered }} />;
  };
}

function translationGeneratorAssociative(
  dictionary: PartialDictionary,
  i18nOverrides?: Record<string, string>,
): (key: TranslationKey, params: Record<string, string | number | string[]>) => string {
  return (key, params) => {
    const translated: string = lookupKey(key, dictionary, i18nOverrides);

    return translated.replaceAll(/%%(\w+)/g, (_, match: string) => {
      const replacement = params[match] ?? match;
      if (Array.isArray(replacement)) {
        return replacement.join(', ');
      }

      return String(replacement);
    });
  };
}

function translatorGeneratorReact(
  dictionary: PartialDictionary,
  i18nOverrides?: Record<string, string>,
): (key: TranslationKey, ...params: ReactNode[]) => ReactNode[] {
  return (key: TranslationKey, ...params: ReactNode[]) => {
    const translated: string = lookupKey(key, dictionary, i18nOverrides);

    const replacementMapping = Object.fromEntries(params.map((v, i) => [`%%${i}`, v]));
    const constFragments = Array.from(translated.split(/%%\d/g));
    const dynamicFragments = Array.from(translated.match(/%%\d/g) ?? []);

    return constFragments
      .flatMap((v, i) => [v, replacementMapping[dynamicFragments[i]!]])
      .slice(0, -1);
  };
}

export type StringTranslator = (key: TranslationKey, ...params: (string | number)[]) => string;
type ReactTranslator = (key: TranslationKey, ...params: ReactNode[]) => ReactNode[];
type MarkdownTranslator = (
  key: TranslationKey,
  ...params: (string | number)[]
) => React.JSX.Element;

/**
 * @deprecated This should basically never be used but is a workaround when user provided strings are mixed with translations.
 */
export const launder = (key: string): TranslationKey => key as TranslationKey;
/**
 * @deprecated This should basically never be used but is a workaround when user provided strings are mixed with translations.
 */
export const launderUndef = (key: string | undefined): TranslationKey | undefined =>
  key as TranslationKey | undefined;

interface TranslationContextValue {
  readonly t: ReactTranslator;
  readonly ts: StringTranslator;
  /**
   * Translate a key with markdown support.
   */
  readonly tsmd: MarkdownTranslator;
  /**
   * Translates a key with parameters in an associative way.
   */
  readonly tsa: (key: TranslationKey, params: Record<string, string | number | string[]>) => string;
  readonly language: string;
  readonly setLanguage: (language: string) => void;
  readonly currency: (cents: number) => string;
  readonly availableLanguages: { key: string; label: string }[];
}

const TranslationContext = createContext<TranslationContextValue | undefined>(undefined);

interface TranslationContextProviderProps {
  readonly children: ReactNode;
}

export const TranslationConsumer = TranslationContext.Consumer;

export const TranslationProvider: FC<TranslationContextProviderProps> = ({ children }) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const { i18nOverrides, currencySymbol } = useConfig() ?? {};
  const [language, setLanguage] = useState<string>(
    window.localStorage.getItem('language') || window.navigator.language || 'en-US',
  );

  const context = useMemo((): TranslationContextValue => {
    const dictionary: PartialDictionary =
      dictionaries[language] ?? dictionaries[language.split('-')[0]!] ?? en_us;

    return {
      t: translatorGeneratorReact(dictionary, i18nOverrides),
      ts: translatorGenerator(dictionary, i18nOverrides),
      tsa: translationGeneratorAssociative(dictionary, i18nOverrides),
      tsmd: translatorGeneratorMardown(dictionary, i18nOverrides),
      language,
      currency: (cents: number) => `${currencySymbol}${(cents / 100).toFixed(2)}`,
      setLanguage: (newLanguage: string) => {
        window.localStorage.setItem('language', newLanguage);
        setLanguage(newLanguage);
        window.location.reload();
      },
      availableLanguages,
    };
  }, [language, i18nOverrides, currencySymbol]);

  return <TranslationContext.Provider value={context}>{children}</TranslationContext.Provider>;
};

export function useTranslation(): TranslationContextValue {
  const res = useContext(TranslationContext);
  if (!res) {
    throw new Error('Missing context');
  }

  return res;
}
